import { UNLIMITED_PAGINATION_PARAMS } from 'constants/pagination';
import { Resources } from 'constants/resources';

import { useEffect } from 'react';

import { GatewayRulesFormValues } from 'pages/GatewayRules/components/GatewayRulesForm/hooks/useGatewayRulesForm';
import { useGetList } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { Gateway } from 'types/embedded/gateway';
import { cleanEmpty } from 'utils/cleanEmpty';

import { useGatewayRulesDictionaryContext } from '../../context/GatewayRulesDictionaryContext';

type Params = {
  mappingIndex: number;
  onChangeDirection: (index: number) => void;
};

export function useAutoSelectDirection({ mappingIndex, onChangeDirection }: Params) {
  const { watch, setValue } = useFormContext<GatewayRulesFormValues>();

  const methodWatched = watch(`mappings.${mappingIndex}.method`);
  const directionWatched = watch(`mappings.${mappingIndex}.direction`);
  const canAutoSelectDirection = Boolean(methodWatched) && !directionWatched;

  // Шлюзы для получения доступного направления
  const { data: gatewaysForGuess } = useGetList<Gateway>(
    Resources.Gateways,
    {
      pagination: UNLIMITED_PAGINATION_PARAMS,
      filter: cleanEmpty({
        'gatewayMethods.method': methodWatched || undefined,
        status: ['active', 'test', 'pilot'],
      }),
    },
    { enabled: canAutoSelectDirection },
  );
  const { directions } = useGatewayRulesDictionaryContext();

  useEffect(() => {
    if (!canAutoSelectDirection || !gatewaysForGuess?.length || !directions?.length) {
      return;
    }

    const gatewaysDirectionValues = gatewaysForGuess.map((g) => g.direction?.value).filter(Boolean);
    if (gatewaysDirectionValues.includes('payment_payout')) {
      return;
    }

    const uniqueValues = Array.from(new Set(gatewaysDirectionValues));
    if (uniqueValues.length !== 1) {
      return;
    }

    const matchedDirection = directions.find((d) => d.value === uniqueValues[0]);

    if (!matchedDirection) {
      return;
    }

    setValue(`mappings.${mappingIndex}.direction`, matchedDirection?.['@id'], {
      shouldDirty: true,
      shouldValidate: true,
    });
    onChangeDirection(mappingIndex);
  }, [gatewaysForGuess, directionWatched, directions, canAutoSelectDirection]);
}
